// Library Methods
import { useTranslation } from "react-i18next";

// Components
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import SummaryCard, { NothingToSummarise } from "../../shared/SummaryCard";
import { FigureRow, Figures } from "../../shared/FigureRow";
import {
  DistanceKindType,
  FigureColumn,
  FigureColumns,
} from "../../shared/FigureColumn";
import AlignmentFigure from "../../shared/figures/AlignmentFigure";
import SubjectiveFigure from "../../shared/figures/SubjectiveFigure";
import TorsionFigure, {
  TorsionScopeType,
} from "../../shared/figures/TorsionFigure";

// Types
import {
  ExamSectionReportType,
  ObjectiveAlignmentSummaryType,
  SubjectiveAngleSummaryType,
  SynoptophoreAxisSummaryType,
} from "src/services/exams/types";

// Utils
import { num } from "../../shared/formatters";

type SummaryType = NonNullable<
  ExamSectionReportType["postProcessing"]["summary"]
>;

type SummarySectionType = {
  summary: SummaryType | null | undefined;
};

const DISTANCES: Array<DistanceKindType> = ["far", "near"];

// A turn is worth drawing only once it is a turn. Below the exam's own threshold `direction` is
// "None" and there is nothing to point at.
const hasDeviation = (axis: SynoptophoreAxisSummaryType | undefined): boolean =>
  Boolean(axis?.direction && axis.direction !== "None");

const Sentence = ({ children }: { children: React.ReactNode }) => (
  <Typography fontSize="18px" fontWeight={700} sx={{ lineHeight: 1.35 }}>
    {children}
  </Typography>
);

const RunLine = ({ children }: { children: React.ReactNode }) => (
  <Typography fontSize="12px" color="#5B6D7A" sx={{ lineHeight: 1.5 }}>
    {children}
  </Typography>
);

const Big = ({
  children,
  warm = false,
}: {
  children: React.ReactNode;
  warm?: boolean;
}) => (
  <Typography
    fontSize="18px"
    fontWeight={700}
    color={warm ? "#E2772E" : "#202338"}
    sx={{ lineHeight: 1.35 }}
  >
    {children}
  </Typography>
);

const Meta = ({ children }: { children: React.ReactNode }) => (
  <Typography fontSize="12px" color="#5B6D7A">
    {children}
  </Typography>
);

/**
 * Where the eyes point, drawn. One column per axis per distance that has a turn to show.
 */
const AlignmentCard = ({
  alignment,
}: {
  alignment:
    | {
        far: ObjectiveAlignmentSummaryType | null;
        near: ObjectiveAlignmentSummaryType | null;
      }
    | null
    | undefined;
}) => {
  const { t } = useTranslation();

  const columns = DISTANCES.flatMap((distance) => {
    const data = alignment?.[distance];
    if (!data) return [];

    return (
      [
        { key: "horizontal", label: "Horizontal Deviation" },
        { key: "vertical", label: "Vertical Deviation" },
      ] as const
    ).flatMap((axis) => {
      const value = data[axis.key];
      if (!hasDeviation(value)) return [];

      return [
        {
          id: `${axis.key}-${distance}`,
          label: axis.label,
          distance,
          value,
          pattern: value.pattern,
        },
      ];
    });
  });

  // Which eye is the one that turns, from whichever distance reported it. Said once in the
  // run line rather than printed as a one-word field whose word only means anything next to
  // the other fields.
  const laterality = DISTANCES.map(
    (distance) => alignment?.[distance]?.laterality
  ).find((value) => value && value !== "None");

  return (
    <SummaryCard
      title={t("Eye alignment — what the headset measured")}
      cautions={null}
    >
      <Box
        sx={{ pb: "8px", display: "flex", flexDirection: "column", gap: "8px" }}
      >
        {columns.length === 0 ? (
          <NothingToSummarise />
        ) : (
          <>
            <FigureColumns>
              {columns.map((column) => (
                <FigureColumn
                  key={column.id}
                  title={t(column.label)}
                  distance={column.distance}
                >
                  <Box sx={{ maxWidth: "150px", width: "100%", mx: "auto" }}>
                    <AlignmentFigure
                      prismDioptres={Math.abs(column.value.prismDioptres ?? 0)}
                      direction={column.value.direction ?? ""}
                      caption={t(
                        column.value.direction === "Eso"
                          ? "right eye turns in"
                          : column.value.direction === "Exo"
                            ? "right eye turns out"
                            : column.value.direction ?? ""
                      )}
                      near={column.distance === "near"}
                    />
                  </Box>
                  <Big>
                    {num(Math.abs(column.value.prismDioptres ?? 0), 1, "Δ")}{" "}
                    {t(column.value.direction ?? "")}
                  </Big>
                  {column.pattern && column.pattern !== "None" && (
                    <Meta>{t(column.pattern)}</Meta>
                  )}
                </FigureColumn>
              ))}
            </FigureColumns>

            <RunLine>
              {laterality &&
                `${t("Laterality: the {{eye}} eye is the one that turns.", {
                  eye: t(laterality).toLowerCase(),
                })} `}
              {t(
                "Horizontal Deviation is the side-to-side turn and Vertical Deviation the up-and-down one; eso means turned in towards the nose and exo means turned out. A tropia is turned all of the time, a phoria only when the other eye is covered, and an intermittent tropia is turned some of the time."
              )}
            </RunLine>
          </>
        )}
      </Box>
    </SummaryCard>
  );
};

/**
 * What the patient said, and whether it matches what was measured.
 */
const SubjectiveCard = ({
  subjective,
  alignment,
}: {
  subjective:
    | {
        far: SubjectiveAngleSummaryType | null;
        near: SubjectiveAngleSummaryType | null;
      }
    | null
    | undefined;
  alignment:
    | {
        far: ObjectiveAlignmentSummaryType | null;
        near: ObjectiveAlignmentSummaryType | null;
      }
    | null
    | undefined;
}) => {
  const { t } = useTranslation();

  const columns = DISTANCES.flatMap((distance) => {
    const data = subjective?.[distance];
    if (!data || !hasDeviation(data.horizontal)) return [];
    return [{ distance, data }];
  });

  // The figures below the columns describe the comparison itself, so they are read off
  // whichever distance reported one.
  const reported = columns[0]?.data;

  return (
    <SummaryCard title={t("What the patient reported")} cautions={null}>
      <Box
        sx={{ pb: "8px", display: "flex", flexDirection: "column", gap: "8px" }}
      >
        {columns.length === 0 ? (
          <NothingToSummarise />
        ) : (
          <>
            <Sentence>
              {t(
                "The patient was shown a frame to one eye and a cross to the other and asked to put the cross in the frame."
              )}
            </Sentence>

            <FigureColumns>
              {columns.map((column) => (
                <FigureColumn
                  key={column.distance}
                  title={t("Subjective Angle")}
                  distance={column.distance}
                >
                  <Box sx={{ maxWidth: "290px", width: "100%", mx: "auto" }}>
                    <SubjectiveFigure
                      subjectivePrismDioptres={
                        column.data.horizontal.prismDioptres ?? 0
                      }
                      objectivePrismDioptres={
                        alignment?.[column.distance]?.horizontal
                          .prismDioptres ?? null
                      }
                      direction={column.data.horizontal.direction ?? ""}
                    />
                  </Box>
                  <Meta>
                    {t(
                      "Where the patient put the cross. The grey dot is where the headset measured the eye to be."
                    )}
                  </Meta>
                  <Big>
                    {num(
                      Math.abs(column.data.horizontal.prismDioptres ?? 0),
                      1,
                      "Δ"
                    )}{" "}
                    {t(column.data.horizontal.direction ?? "")}
                  </Big>
                </FigureColumn>
              ))}
            </FigureColumns>

            <Figures>
              <FigureRow
                label={t("Correspondence")}
                value={
                  reported?.correspondence
                    ? t(reported.correspondence)
                    : t("N/A")
                }
                sub={t("The patient's answer and the measurement agree")}
              />
              <FigureRow
                label={t("Angle of Anomaly")}
                value={num(reported?.angleOfAnomalyPrismDioptres, 1, "Δ")}
                sub={t(
                  "The gap between what was measured and what the patient reported"
                )}
              />
              <FigureRow
                label={t("Suppressing Eye")}
                value={
                  reported?.suppressingEye && reported.suppressingEye !== "None"
                    ? t(reported.suppressingEye)
                    : t("None")
                }
                sub={t(
                  "Whether either eye was switched off while the patient answered"
                )}
              />
              {/* A size grade off `SubjectiveTargetGrade`, which was printing as a bare enum
                  name. It says how small the target could get before one eye stopped
                  contributing. */}
              <FigureRow
                label={t("Smallest target still seen by both eyes")}
                value={
                  reported?.perceptionFloor
                    ? t(reported.perceptionFloor)
                    : t("N/A")
                }
                sub={t(
                  "The smallest rung of the ladder the patient still fused"
                )}
              />
            </Figures>

            <RunLine>
              {t(
                "When the two numbers agree, each eye is using the part of the retina it was built to use. When they differ, the brain has re-aimed one eye to cope with the turn, and the gap between them is the Angle of Anomaly."
              )}
            </RunLine>
          </>
        )}
      </Box>
    </SummaryCard>
  );
};

/**
 * Eye tilt, drawn on the eyes. The card says what was measured and does not say what counts
 * as normal -- that is not a call the report makes unless it is asked to.
 */
const TorsionCard = ({
  cyclodeviation,
}: {
  cyclodeviation: SummaryType["Cyclodeviation"];
}) => {
  const { t } = useTranslation();

  const columns = (
    [
      { scope: "both", label: "Both eyes together", caption: "both eyes" },
      { scope: "left", label: "Left eye", caption: "left eye" },
      { scope: "right", label: "Right eye", caption: "right eye" },
    ] as Array<{ scope: TorsionScopeType; label: string; caption: string }>
  ).flatMap((column) => {
    const value =
      column.scope === "both"
        ? cyclodeviation?.total
        : column.scope === "left"
          ? cyclodeviation?.leftEye
          : cyclodeviation?.rightEye;

    if (
      !value ||
      value.degrees === null ||
      !value.direction ||
      value.direction === "None"
    ) {
      return [];
    }

    return [{ ...column, degrees: value.degrees, direction: value.direction }];
  });

  return (
    <SummaryCard title={t("Eye tilt")} cautions={null}>
      <Box
        sx={{ pb: "8px", display: "flex", flexDirection: "column", gap: "8px" }}
      >
        {columns.length === 0 ? (
          <NothingToSummarise />
        ) : (
          <>
            <FigureColumns>
              {columns.map((column) => (
                <FigureColumn key={column.scope} title={t(column.label)}>
                  <Box sx={{ maxWidth: "210px", width: "100%", mx: "auto" }}>
                    <TorsionFigure
                      degrees={Math.abs(column.degrees)}
                      direction={column.direction}
                      scope={column.scope}
                      caption={t(column.caption)}
                    />
                  </Box>
                  <Big>
                    {num(Math.abs(column.degrees), 1, "°")}{" "}
                    {t(column.direction)}
                  </Big>
                </FigureColumn>
              ))}
            </FigureColumns>

            <RunLine>
              {t(
                "The dashed line through each eye is upright and the solid one is where the patient set the image, so the angle marked between them is the tilt. Excyclo means the top of the image is turned away from the nose and incyclo means towards it. The lines are drawn at six times the measured angle, because a couple of degrees is invisible at this size; the number is the real one."
              )}
            </RunLine>
          </>
        )}
      </Box>
    </SummaryCard>
  );
};

/**
 * The three cards this section is now: where the eyes point, what the patient said about it,
 * and how the eyes are tilted.
 *
 * No cautions are rendered. Nothing on the list described the patient -- what matters is said
 * on the field it is about -- and the codes were reaching the screen as raw keys. They keep
 * being emitted and keep riding on the payload.
 */
const SummarySection = ({ summary }: SummarySectionType) => {
  if (!summary) return null;

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: "8px" }}>
      <AlignmentCard alignment={summary.ObjectiveAlignment} />
      <SubjectiveCard
        subjective={summary.SubjectiveAngle}
        alignment={summary.ObjectiveAlignment}
      />
      <TorsionCard cyclodeviation={summary.Cyclodeviation} />
    </Box>
  );
};

export default SummarySection;
